import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, BookOpen, Clock } from 'lucide-react';
import Footer from "../../../components/landing/Footer";
import { motion } from "framer-motion";
import { Button } from "../../../components/ui/button";

const placeholderPosts = [
  {
    title: "Top 5 PG Accommodations in Hyderabad for Working Professionals",
    excerpt: "Hyderabad's IT corridor is booming, and so is demand for quality PG stays. We explore the best options near HITEC City, Kondapur, and Gachibowli.",
    category: "City Guides",
    readTime: "4 min read",
    date: "Coming Soon",
  },
  {
    title: "How to Choose the Right PG: A Complete Checklist",
    excerpt: "From security deposits to amenities, learn what to look for before signing up for a paying guest accommodation to avoid common pitfalls.",
    category: "Tips & Advice",
    readTime: "6 min read",
    date: "Coming Soon",
  },
  {
    title: "Budget Travel in Bharat: Making the Most of Affordable Stays",
    excerpt: "Discover how Social Bunkr is helping thousands of students, freshers, and travellers live comfortably without breaking the bank.",
    category: "Lifestyle",
    readTime: "3 min read",
    date: "Coming Soon",
  },
  {
    title: "Host Guide: Maximise Your Rental Income with Social Bunkr",
    excerpt: "Whether you have a spare room or a full property, learn how to list on Social Bunkr and attract the right tenants.",
    category: "For Hosts",
    readTime: "5 min read",
    date: "Coming Soon",
  },
];

const Blog: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border"
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 md:h-20">
            <motion.div className="flex items-center gap-2" whileHover={{ scale: 1.02 }}>
              <Link to="/" className="text-2xl md:text-3xl font-bold">
                <span className="text-primary">Social</span>
                <span className="bg-secondary text-secondary-foreground px-2 py-0.5 rounded-md ml-1">Bunkr</span>
              </Link>
            </motion.div>
            <nav className="hidden md:flex items-center gap-8">
              {["Home", "About", "Contact"].map((item) => (
                <Link key={item} to={item === "Home" ? "/" : `/${item.toLowerCase()}`}
                  className="text-foreground/80 hover:text-primary font-medium transition-colors">
                  {item}
                </Link>
              ))}
            </nav>
            <div className="hidden md:flex items-center gap-4">
              <Link to="/login"><Button variant="ghost" className="text-foreground hover:text-primary">Login</Button></Link>
              <Link to="/signup"><Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6">Sign Up</Button></Link>
            </div>
            <button className="md:hidden p-2" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
          {isMobileMenuOpen && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="md:hidden py-4 space-y-3">
              {["Home", "About", "Contact"].map((item) => (
                <Link key={item} to={item === "Home" ? "/" : `/${item.toLowerCase()}`}
                  className="block text-foreground/80 hover:text-primary font-medium transition-colors py-2">
                  {item}
                </Link>
              ))}
            </motion.div>
          )}
        </div>
      </motion.header>

      <main className="flex-grow pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold text-foreground mb-4">Social Bunkr Blog</h1>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                Tips, guides, and stories about affordable living, travel, and finding your perfect stay in Bharat.
              </p>
              <div className="mt-4 inline-flex items-center gap-2 bg-primary/10 text-primary rounded-full px-4 py-2 text-sm font-medium">
                <BookOpen className="w-4 h-4" />
                Blog launching soon - stay tuned!
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {placeholderPosts.map((post) => (
                <motion.div
                  key={post.title}
                  whileHover={{ y: -4 }}
                  className="bg-background rounded-2xl shadow-sm p-6 cursor-default opacity-75"
                >
                  <span className="inline-block text-xs font-semibold text-primary bg-primary/10 rounded-full px-3 py-1 mb-3">
                    {post.category}
                  </span>
                  <h2 className="text-lg font-semibold text-foreground mb-2 line-clamp-2">{post.title}</h2>
                  <p className="text-foreground/70 text-sm leading-relaxed mb-4 line-clamp-3">{post.excerpt}</p>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {post.readTime}
                    </span>
                    <span className="bg-muted rounded-full px-2 py-0.5">{post.date}</span>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="mt-12 bg-background rounded-2xl shadow-sm p-8 text-center">
              <h2 className="text-xl font-semibold text-foreground mb-2">Want to be notified when we launch?</h2>
              <p className="text-muted-foreground mb-6">Be the first to read our articles on affordable living and travel in Bharat.</p>
              <Link to="/contact">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8">
                  Get in Touch
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Blog;
